import { forwardRef } from "react";
import { cva, VariantProps } from 'class-variance-authority';
import { twMerge } from "tailwind-merge";
import { cn } from "../../utils/cn";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { replacePeriodToUnderscore } from "@/common/utils/transformers";

const textFieldVariants = cva(
  'p-4 h-[52px] w-full text-sm rounded-md border bg-neutral-op-8 outline-none border border-transparent pointer-events-auto',
  {
    variants: {
      variant: {
        default: 'text-white-800',
        badge: 'flex w-full h-[32px] justify-center bg-transparent placeholder:text-xs rounded-xl placeholder:text-white-800 placeholder:italic px-4',
        pagination: 'p-0 h-4 px-1 max-w-8 min-w-[30px] rounded-[2px] bg-blue-700',
        disabled: 'text-white-300',
        error: 'text-white-800 border-danger',
        filter: 'bg-white-100 text-xs text-white h-8 py-0 rounded-xl shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.2)] placeholder:text-white-800 placeholder:italic',
      }
    },
    defaultVariants: {
      variant: 'default',
    }
  }
);

export interface TextFieldProps extends React.HTMLAttributes<HTMLInputElement>, VariantProps<typeof textFieldVariants> {
  label?: string,
  markAsRequired?: boolean,
  type?: string,
  min?: string | number,
  max?: string | number,
  disabled?: boolean,
  maxLength?: number,
  RightIcon?: JSX.Element;
  LeftIcon?: JSX.Element | string;
  labelClass?: string;
  placeholder?: string;
  errorMessage?: string;
  value?: string | null;
  onIconClick?: React.MouseEventHandler<HTMLDivElement>,
  name?: string,
  isLoading?: boolean
  valueClassName?: string
  dataTestid?: string
  leftIconSkipTranslate?: boolean;
  rightIconSkipTranslate?: boolean;
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(({

  label,
  markAsRequired,
  variant,
  className,
  disabled = false,
  maxLength = 64,
  placeholder,
  RightIcon,
  labelClass = "",
  LeftIcon,
  errorMessage,
  value,
  onIconClick,
  type = 'text',
  isLoading,
  valueClassName,
  dataTestid,
  leftIconSkipTranslate,
  rightIconSkipTranslate,
  name,
  ...props
}, ref) => {

  return (
    <div className={cn(className, variant === 'badge' && !!value && 'bg-[#49565C]')}>
      <label className={`${labelClass} relative flex flex-col items-start justify-center pointer-events-none`}>
        {label ? <p className={twMerge('text-xs', disabled ? '' : '', 'mb-1')}>{label}{markAsRequired && <span className="text-danger-soft"> *</span>}</p> : null}
        {
          RightIcon
            ?
            <div
              className={twMerge(
                'absolute right-0 bottom-0 -translate-x-1/2 -translate-y-[calc(50%+4px)] pointer-events-auto',
                variant === 'filter' && '-translate-y-[calc(50%+2px)]',
                !rightIconSkipTranslate && '-translate-x-1/2',
                onIconClick && 'cursor-pointer'
              )}
              onClick={onIconClick}
            >
              {RightIcon}
            </div>
            : null
        }
        {
          isLoading &&
          <div
            className={twMerge(
              'absolute right-0 bottom-0 -translate-x-1/2 -translate-y-[calc(50%+4px)] pointer-events-auto',
              variant === 'filter' && '-translate-y-[calc(50%+2px)]',
              onIconClick && 'cursor-pointer'
            )}
          >
            <div className="w-4 h-4 border-4 border-dashed rounded-full animate-spin border-blue-800 mb-1 mr-1" />
          </div>
        }
        {
          LeftIcon
            ?
            <div
              className={twMerge(
                'absolute left-0 bottom-0 translate-x-1/2 -translate-y-[calc(50%+4px)] pointer-events-auto',
                variant === 'filter' && '-translate-y-[calc(50%+2px)]',
                !leftIconSkipTranslate && 'translate-x-1/2',
                onIconClick && 'cursor-pointer'
              )}
              onClick={onIconClick}
            >
              {LeftIcon}
            </div>
            : null
        }
        <input
          ref={ref}
          type={type}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={placeholder}
          spellCheck={false}
          value={value as string | undefined}
          className={cn(textFieldVariants({ variant: disabled ? 'disabled' : variant }), LeftIcon && (variant === 'filter' ? 'pl-7' : 'pl-10'), valueClassName)}
          name={name}
          data-testid={dataTestid || (name ? replacePeriodToUnderscore(name) + '_input' : '')}
          {...props}
        />
      </label>
      {!!errorMessage?.length && <ErrorMessage name={name} message={errorMessage} />}
    </div>
  );
});

export default TextField;
